import { useState } from "react";
import { MessageCircle, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { formatRelativeTime } from "@/lib/utils";
import type { ChatSessionOut } from "@/hooks/use-chat";

interface ChatSidebarSessionsProps {
  sessions: ChatSessionOut[];
  activeSessionId?: string;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
  onDeleteSession: (id: string) => void;
}

export default function ChatSidebarSessions({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewSession,
  onDeleteSession,
}: ChatSidebarSessionsProps) {
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  return (
    <div className="w-64 flex-shrink-0 bg-surface-100 border-r border-line flex flex-col h-full">
      <div className="p-3 border-b border-line">
        <button
          onClick={onNewSession}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-brand rounded hover:bg-brand-hover transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nueva conversación
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 ? (
          <div className="p-4 text-center text-sm text-content-muted">
            No hay conversaciones
          </div>
        ) : (
          <div className="py-1">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`group flex items-start gap-2 px-3 py-2.5 hover:bg-surface-200 transition-colors cursor-pointer ${
                  activeSessionId === session.id
                    ? "bg-brand/10 border-r-2 border-brand"
                    : ""
                }`}
                onClick={() => onSelectSession(session.id)}
              >
                <MessageCircle
                  className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    activeSessionId === session.id
                      ? "text-brand"
                      : "text-content-muted/60"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm truncate ${
                      activeSessionId === session.id
                        ? "font-medium text-brand-hover"
                        : "text-content"
                    }`}
                  >
                    {session.titulo || "Sin título"}
                  </p>
                  <p className="text-xs text-content-muted/70 mt-0.5">
                    {formatRelativeTime(session.updated_at)}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingDelete(session.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-content-muted hover:text-danger transition-all flex-shrink-0"
                  aria-label="Eliminar conversación"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Eliminar conversación"
        description="Se eliminará la conversación y todos sus mensajes. Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        variant="destructive"
        onConfirm={() => {
          if (pendingDelete) onDeleteSession(pendingDelete);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
